import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { api } from '../api/client';
import { investigatorBase as base } from '../api/investigator';
export default function InvestigatorSettings({ storeId, nvr, canEdit, globalAdmin, onUpdated, initiallyOpen = false }) {
    const [open, setOpen] = useState(initiallyOpen);
    const [data, setData] = useState(null);
    const [zone, setZone] = useState('');
    const [payers, setPayers] = useState(null);
    const [payer, setPayer] = useState('');
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const mounted = useRef(true);
    const lock = useRef(false);
    useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
    useEffect(() => {
        if (!open || !nvr)
            return;
        const controller = new AbortController();
        api.get(`${base}/stores/${storeId}/nvrs/${nvr}/metadata`, controller.signal).then(m => { setData(m); setZone(m.timezone.timezone ?? ''); }).catch(e => { if (!controller.signal.aborted)
            setError(String(e)); });
        if (globalAdmin)
            api.get(`${base}/stores/${storeId}/payers`, controller.signal).then(p => { setPayers(p); setPayer(p.mapping?.account_id ?? ''); }).catch(e => { if (!controller.signal.aborted)
                setError(String(e)); });
        return () => controller.abort();
    }, [open, nvr, storeId, globalAdmin]);
    async function save(action) {
        if (lock.current)
            return;
        lock.current = true;
        setBusy(true);
        setError('');
        try {
            await action();
            const value = await api.get(`${base}/stores/${storeId}/nvrs/${nvr}/metadata`);
            if (mounted.current) {
                setData(value);
                onUpdated();
            }
        }
        catch (e) {
            if (mounted.current)
                setError(String(e));
        }
        finally {
            lock.current = false;
            if (mounted.current)
                setBusy(false);
        }
    }
    return _jsxs("details", { className: 'investigator-settings', open: open, onToggle: e => setOpen(e.currentTarget.open), children: [_jsx("summary", { children: "Store configuration" }), error && _jsx("p", { role: 'alert', children: error }), data && _jsxs(_Fragment, { children: [_jsxs("label", { children: ["Store timezone", _jsx("input", { value: zone, placeholder: 'America/Chicago', disabled: !canEdit, onChange: e => setZone(e.target.value) })] }), canEdit && _jsx("button", { className: 'ghost', disabled: busy || !zone.trim(), onClick: () => void save(() => api.patch(`${base}/stores/${storeId}/timezone`, { timezone: zone, expected_version: data.timezone.version })), children: "Save timezone" }), _jsx("p", { children: "Timezone changes affect new windows. Saved investigations retain their resolved UTC window." })] }), globalAdmin && payers && _jsxs(_Fragment, { children: [_jsxs("label", { children: ["Billing account", _jsxs("select", { value: payer, onChange: e => setPayer(e.target.value), children: [_jsx("option", { value: '', children: "Select an account" }), payers.accounts.map(p => _jsxs("option", { value: p.id, disabled: !p.active, children: [p.name, p.active ? '' : ' (inactive)'] }, p.id))] })] }), _jsx("p", { children: "Billing grouping does not grant video access. Existing runs keep their original payer." }), _jsx("button", { className: 'ghost', disabled: busy || !payer, onClick: () => void save(() => api.post(`${base}/stores/${storeId}/payer`, { account: payer })), children: "Save payer" }), payers.next_cursor && _jsx("button", { className: 'ghost', disabled: busy, onClick: () => void save(async () => { const page = await api.get(`${base}/stores/${storeId}/payers?after=${payers.next_cursor}`); if (mounted.current)
                            setPayers({ ...page, accounts: [...payers.accounts, ...page.accounts] }); }), children: "More accounts" })] })] });
}
